// p5.js Function Signatures with Parameter Info
// Based on p5.js official documentation
// This is the default docs dataset for the current p5 version; p5Docs wraps this so we can
// later swap in generated, per-version docs files without touching editor or Monaco wiring.

export const p5Signatures = {
  // Drawing Functions
  circle: {
    label: 'circle(x, y, d)',
    documentation: 'Draws a circle to the screen. A circle is a simple closed shape. It is the set of all points in a plane that are at a given distance from a given point, the center.',
    parameters: [
      { label: 'x', documentation: 'x-coordinate of the center of the circle' },
      { label: 'y', documentation: 'y-coordinate of the center of the circle' },
      { label: 'd', documentation: 'diameter of the circle' }
    ]
  },
  
  rect: {
    label: 'rect(x, y, w, h, [tl], [tr], [br], [bl])',
    documentation: 'Draws a rectangle to the screen. A rectangle is a four-sided shape with every angle at ninety degrees.',
    parameters: [
      { label: 'x', documentation: 'x-coordinate of the rectangle' },
      { label: 'y', documentation: 'y-coordinate of the rectangle' },
      { label: 'w', documentation: 'width of the rectangle' },
      { label: 'h', documentation: 'height of the rectangle' },
      { label: 'tl', documentation: 'optional top-left corner radius' },
      { label: 'tr', documentation: 'optional top-right corner radius' },
      { label: 'br', documentation: 'optional bottom-right corner radius' },
      { label: 'bl', documentation: 'optional bottom-left corner radius' }
    ]
  },
  
  ellipse: {
    label: 'ellipse(x, y, w, [h])',
    documentation: 'Draws an ellipse (oval) to the screen. An ellipse with equal width and height is a circle.',
    parameters: [
      { label: 'x', documentation: 'x-coordinate of the center of the ellipse' },
      { label: 'y', documentation: 'y-coordinate of the center of the ellipse' },
      { label: 'w', documentation: 'width of the ellipse' },
      { label: 'h', documentation: 'height of the ellipse (defaults to w)' }
    ]
  },
  
  line: {
    label: 'line(x1, y1, x2, y2)',
    documentation: 'Draws a line (a direct path between two points) to the screen.',
    parameters: [
      { label: 'x1', documentation: 'the x-coordinate of the first point' },
      { label: 'y1', documentation: 'the y-coordinate of the first point' },
      { label: 'x2', documentation: 'the x-coordinate of the second point' },
      { label: 'y2', documentation: 'the y-coordinate of the second point' }
    ]
  },
  
  triangle: {
    label: 'triangle(x1, y1, x2, y2, x3, y3)',
    documentation: 'Draws a triangle to the screen.',
    parameters: [
      { label: 'x1', documentation: 'x-coordinate of the first point' },
      { label: 'y1', documentation: 'y-coordinate of the first point' },
      { label: 'x2', documentation: 'x-coordinate of the second point' },
      { label: 'y2', documentation: 'y-coordinate of the second point' },
      { label: 'x3', documentation: 'x-coordinate of the third point' },
      { label: 'y3', documentation: 'y-coordinate of the third point' }
    ]
  },
  
  // Color Functions
  background: {
    label: 'background(v1, [v2], [v3], [alpha])',
    documentation: 'Sets the color used for the background of the canvas. The default background is transparent.',
    parameters: [
      { label: 'v1', documentation: 'red or hue value (0-255), or a color string/object' },
      { label: 'v2', documentation: 'green or saturation value (optional)' },
      { label: 'v3', documentation: 'blue or brightness value (optional)' },
      { label: 'alpha', documentation: 'opacity value (0-255, optional)' }
    ]
  },
  
  fill: {
    label: 'fill(v1, [v2], [v3], [alpha])',
    documentation: 'Sets the color used to fill shapes. For example, if you run fill(204, 102, 0), all subsequent shapes will be filled with orange.',
    parameters: [
      { label: 'v1', documentation: 'red or hue value (0-255), or a color string/object' },
      { label: 'v2', documentation: 'green or saturation value (optional)' },
      { label: 'v3', documentation: 'blue or brightness value (optional)' },
      { label: 'alpha', documentation: 'opacity value (0-255, optional)' }
    ]
  },
  
  stroke: {
    label: 'stroke(v1, [v2], [v3], [alpha])',
    documentation: 'Sets the color used to draw lines and borders around shapes.',
    parameters: [
      { label: 'v1', documentation: 'red or hue value (0-255), or a color string/object' },
      { label: 'v2', documentation: 'green or saturation value (optional)' },
      { label: 'v3', documentation: 'blue or brightness value (optional)' },
      { label: 'alpha', documentation: 'opacity value (0-255, optional)' }
    ]
  },
  
  strokeWeight: {
    label: 'strokeWeight(weight)',
    documentation: 'Sets the width of the stroke used for lines, points, and the border around shapes.',
    parameters: [
      { label: 'weight', documentation: 'the weight (in pixels) of the stroke' }
    ]
  },
  
  // Environment
  createCanvas: {
    label: 'createCanvas(w, h, [renderer])',
    documentation: 'Creates a canvas element in the document, and sets the dimensions of it in pixels. This method should be called only once at the start of setup.',
    parameters: [
      { label: 'w', documentation: 'width of the canvas' },
      { label: 'h', documentation: 'height of the canvas' },
      { label: 'renderer', documentation: 'either P2D or WEBGL (optional)' }
    ]
  },
  
  // Transform
  translate: {
    label: 'translate(x, y, [z])',
    documentation: 'Specifies an amount to displace objects within the display window.',
    parameters: [
      { label: 'x', documentation: 'left/right translation' },
      { label: 'y', documentation: 'up/down translation' },
      { label: 'z', documentation: 'forward/backward translation (WebGL only)' }
    ]
  },
  
  rotate: {
    label: 'rotate(angle, [axis])',
    documentation: 'Rotates the coordinate system. The angle parameter is specified in radians.',
    parameters: [
      { label: 'angle', documentation: 'the angle of rotation, specified in radians' },
      { label: 'axis', documentation: 'optional axis vector for 3D rotation' }
    ]
  },
  
  scale: {
    label: 'scale(s, [y], [z])',
    documentation: 'Increases or decreases the size of a shape by expanding and contracting vertices.',
    parameters: [
      { label: 's', documentation: 'percentage to scale horizontally (and vertically if only one argument)' },
      { label: 'y', documentation: 'percentage to scale vertically (optional)' },
      { label: 'z', documentation: 'percentage to scale in z-axis (optional, WebGL)' }
    ]
  },
  
  // Math
  random: {
    label: 'random([min], max)',
    documentation: 'Returns a random number fitting within the given parameters.',
    parameters: [
      { label: 'min', documentation: 'the lower bound (optional, inclusive)' },
      { label: 'max', documentation: 'the upper bound (exclusive)' }
    ]
  },
  
  map: {
    label: 'map(value, start1, stop1, start2, stop2, [withinBounds])',
    documentation: 'Re-maps a number from one range to another.',
    parameters: [
      { label: 'value', documentation: 'the incoming value to be converted' },
      { label: 'start1', documentation: 'lower bound of the value\'s current range' },
      { label: 'stop1', documentation: 'upper bound of the value\'s current range' },
      { label: 'start2', documentation: 'lower bound of the value\'s target range' },
      { label: 'stop2', documentation: 'upper bound of the value\'s target range' },
      { label: 'withinBounds', documentation: 'constrain the value to the target range (optional)' }
    ]
  },
  
  constrain: {
    label: 'constrain(n, low, high)',
    documentation: 'Constrains a value between a minimum and maximum value.',
    parameters: [
      { label: 'n', documentation: 'number to constrain' },
      { label: 'low', documentation: 'minimum limit' },
      { label: 'high', documentation: 'maximum limit' }
    ]
  },
  
  // Typography
  text: {
    label: 'text(str, x, y, [x2], [y2])',
    documentation: 'Draws text to the screen. Displays the information specified in the first parameter on the screen in the position specified by the additional parameters.',
    parameters: [
      { label: 'str', documentation: 'the alphanumeric symbols to be displayed' },
      { label: 'x', documentation: 'x-coordinate of text' },
      { label: 'y', documentation: 'y-coordinate of text' },
      { label: 'x2', documentation: 'width of text box (optional)' },
      { label: 'y2', documentation: 'height of text box (optional)' }
    ]
  },
  
  textSize: {
    label: 'textSize(size)',
    documentation: 'Sets the current font size. This size will be used in all subsequent calls to the text() function.',
    parameters: [
      { label: 'size', documentation: 'the size of the letters in units of pixels' }
    ]
  },
  
  // Image
  image: {
    label: 'image(img, x, y, [width], [height])',
    documentation: 'Draws an image to the canvas.',
    parameters: [
      { label: 'img', documentation: 'the image to display' },
      { label: 'x', documentation: 'the x-coordinate of the top-left corner of the image' },
      { label: 'y', documentation: 'the y-coordinate of the top-left corner of the image' },
      { label: 'width', documentation: 'the width to draw the image (optional)' },
      { label: 'height', documentation: 'the height to draw the image (optional)' }
    ]
  }
}

// Get all function names for autocomplete
export const p5FunctionNames = Object.keys(p5Signatures)
